import React, { useState, useEffect, useMemo } from "react";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { classMgmtService } from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import { notificationService } from "services/notificationService";
import Card from "components/card";
import FilterChip from "components/ui/FilterChip";

interface UserItem {
  id: string;
  full_name: string;
  email?: string;
  code?: string;
  role?: string;
}

type TargetMode = "class" | "users";

const ROLE_OPTIONS = ["ALL", "STUDENT", "ALL_STAFF", "MANAGER"] as const;
type RoleFilter = (typeof ROLE_OPTIONS)[number];

const ROLE_FILTER_LABEL_KEYS: Record<RoleFilter, string> = {
  ALL: "notifications.roleFilter.ALL",
  STUDENT: "enum.role.STUDENT",
  ALL_STAFF: "notifications.roleFilter.ALL_STAFF",
  MANAGER: "enum.role.MANAGER",
};

const ROLE_BADGE_CLASSES: Record<string, string> = {
  STUDENT: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  TEACHER: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  TRAINER:
    "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  MANAGER:
    "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
};

interface SendNotificationPageProps {
  // When provided, scope class + user lists to the given center.
  // Used by System Admin acting-as-center-owner.
  centerId?: string;
}

const SendNotificationPage: React.FC<SendNotificationPageProps> = ({
  centerId,
}) => {
  const { t, locale } = useLanguage();
  const { addNotification } = useNotification();

  const [targetMode, setTargetMode] = useState<TargetMode>("class");
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(
    new Set()
  );
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("ALL");
  const [users, setUsers] = useState<UserItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load classes on mount — scoped to selected center when provided.
  useEffect(() => {
    classMgmtService
      .getAll({ skip: 0, limit: 200, center_id: centerId })
      .then((res: any) => {
        const items = res.items ?? res;
        setClasses(Array.isArray(items) ? items : []);
      });
  }, [centerId]);

  // Load enrollments + staff when classes are selected
  useEffect(() => {
    if (targetMode !== "class" || selectedClassIds.size === 0) {
      if (targetMode === "class") {
        setUsers([]);
        setSelectedIds(new Set());
      }
      return;
    }
    setLoadingUsers(true);

    const classIdArray = Array.from(selectedClassIds);

    const promises = classIdArray.map((classId) => {
      const enrollmentPromise = classMgmtService
        .getEnrollments(classId)
        .then((res: any) => {
          const enrollments = res.items ?? res;
          return (Array.isArray(enrollments) ? enrollments : [])
            .map((e: any) => {
              const u = e.user ?? e.student ?? e;
              return {
                id: u.id ?? e.user_id ?? e.student_id,
                full_name: u.full_name ?? u.name ?? "—",
                email: u.email,
                code: u.code,
                role: "STUDENT",
              } as UserItem;
            })
            .filter((s: UserItem) => s.id);
        })
        .catch(() => [] as UserItem[]);

      const staffPromise = classMgmtService
        .getById(classId)
        .then((cls: any) => {
          const managers = cls.managers ?? [];
          return (Array.isArray(managers) ? managers : []).map((m: any) => ({
            id: m.id,
            full_name: m.full_name ?? m.name ?? "—",
            email: m.email,
            code: m.code,
            role: m.role ?? "TEACHER",
          })) as UserItem[];
        })
        .catch(() => [] as UserItem[]);

      return Promise.all([staffPromise, enrollmentPromise]).then(
        ([staff, students]) => [...staff, ...students]
      );
    });

    Promise.all(promises)
      .then((results) => {
        const seen = new Set<string>();
        const combined: UserItem[] = [];
        for (const list of results) {
          for (const u of list) {
            if (!seen.has(u.id)) {
              seen.add(u.id);
              combined.push(u);
            }
          }
        }
        setUsers(combined);
        setSelectedIds(new Set(combined.map((u) => u.id)));
      })
      .finally(() => setLoadingUsers(false));
  }, [targetMode, selectedClassIds]);

  // Load users by role when "select users" mode or role changes
  useEffect(() => {
    if (targetMode !== "users") return;
    setLoadingUsers(true);
    // Resolve center scope: explicit prop wins; fall back to the current user's
    // home center so non-admin users still see only their own members.
    const effectiveCenterId =
      centerId ??
      (JSON.parse(sessionStorage.getItem("current_user") || "{}").center_id as
        | string
        | null) ??
      null;

    const fetchByRole = (role?: string) =>
      userService
        .getAll({
          page: 1,
          limit: 200,
          role,
          center_id: effectiveCenterId,
        })
        .then((res: any) => {
          const items = res.items ?? res;
          return (Array.isArray(items) ? items : []).map((u: any) => ({
            id: u.id,
            full_name: u.full_name ?? u.name ?? "—",
            email: u.email,
            code: u.code,
            role: u.role,
          })) as UserItem[];
        });

    let promise: Promise<UserItem[]>;
    if (roleFilter === "ALL") {
      promise = fetchByRole(undefined);
    } else if (roleFilter === "ALL_STAFF") {
      // Fetch teachers, trainers, and managers in parallel
      promise = Promise.all([
        fetchByRole("TEACHER"),
        fetchByRole("TRAINER"),
        fetchByRole("MANAGER"),
      ]).then(([teachers, trainers, managers]) => {
        const seen = new Set<string>();
        const combined: UserItem[] = [];
        for (const u of [...teachers, ...trainers, ...managers]) {
          if (!seen.has(u.id)) {
            seen.add(u.id);
            combined.push(u);
          }
        }
        return combined;
      });
    } else {
      promise = fetchByRole(roleFilter);
    }

    promise
      .then((list) => {
        setUsers(list);
        setSelectedIds(new Set());
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [centerId, targetMode, roleFilter]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (s) =>
        s.full_name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.code?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleUser = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleClass = (id: string) => {
    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUsers.map((s) => s.id)));
    }
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      addNotification(t("sendNotif.noRecipients"), "error");
      return;
    }
    if (!title.trim()) {
      addNotification(t("sendNotif.noTitle"), "error");
      return;
    }
    if (!message.trim()) {
      addNotification(t("sendNotif.noMessage"), "error");
      return;
    }

    setSending(true);
    try {
      const result = await notificationService.sendToUsers(
        Array.from(selectedIds),
        title.trim(),
        message.trim(),
        undefined,
        scheduledAt ? new Date(scheduledAt).toISOString() : undefined
      );
      addNotification(
        result.scheduled
          ? t("notifications.scheduledSuccess", { count: result.sent_count })
          : t("sendNotif.success", { count: result.sent_count }),
        "success"
      );
      setTitle("");
      setMessage("");
      setScheduledAt("");
      setSelectedIds(new Set());
    } catch {
      addNotification(t("sendNotif.error"), "error");
    } finally {
      setSending(false);
    }
  };

  const roleDisplayName = (role: string) =>
    role in ROLE_BADGE_CLASSES ? t(`enum.role.${role}`) : role;

  const roleLabel = (role: RoleFilter) => t(ROLE_FILTER_LABEL_KEYS[role]);

  return (
    <div className="mt-3 flex flex-col gap-6">
      <h1 className="text-2xl font-bold text-ink">
        {t("sendNotif.title")}
      </h1>

      {/* Target mode selector */}
      <div className="flex gap-3">
        {(["class", "users"] as TargetMode[]).map((mode) => (
          <FilterChip
            key={mode}
            active={targetMode === mode}
            onClick={() => {
              setTargetMode(mode);
              setUsers([]);
              setSelectedIds(new Set());
              setSearch("");
              setSelectedClassIds(new Set());
            }}
          >
            {mode === "class"
              ? t("sendNotif.targetByClass")
              : t("sendNotif.targetByUser")}
          </FilterChip>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: User selection */}
        <Card extra="p-5">
          {/* Class multi-select */}
          {targetMode === "class" && (
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-ink-muted">
                {t("sendNotif.selectClass")}
                {selectedClassIds.size > 0 && (
                  <span className="ml-2 text-xs font-normal text-ink-subtle">
                    {t("notifications.selectedCount", {
                      count: selectedClassIds.size,
                    })}
                  </span>
                )}
              </label>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-line">
                {classes.map((c: any) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-3 border-b border-line px-3 py-2 hover:bg-surface-hover"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClassIds.has(c.id)}
                      onChange={() => toggleClass(c.id)}
                      className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-400"
                    />
                    <span className="text-sm text-ink">
                      {c.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Role filter (users mode) */}
          {targetMode === "users" && (
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-ink-muted">
                {t("sendNotif.roleLabel")}
              </label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((role) => (
                  <FilterChip
                    key={role}
                    active={roleFilter === role}
                    onClick={() => {
                      setRoleFilter(role);
                      setSelectedIds(new Set());
                      setSearch("");
                    }}
                  >
                    {roleLabel(role)}
                  </FilterChip>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mb-3">
            <input
              type="text"
              placeholder={t("sendNotif.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            />
          </div>

          {/* Select all / count */}
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-ink-muted">
              {t("sendNotif.userList")} ({selectedIds.size}{" "}
              {t("sendNotif.selected")})
            </span>
            <button
              onClick={toggleAll}
              className="text-sm font-medium text-brand-500 hover:text-brand-600"
            >
              {selectedIds.size === filteredUsers.length
                ? t("sendNotif.deselectAll")
                : t("sendNotif.selectAll")}
            </button>
          </div>

          {/* User list */}
          <div className="max-h-80 overflow-y-auto rounded-lg border border-line">
            {loadingUsers ? (
              <div className="p-4 text-center text-sm text-ink-subtle">...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-sm text-ink-subtle">
                {t("sendNotif.noUsers")}
              </div>
            ) : (
              filteredUsers.map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-3 border-b border-line px-3 py-2 hover:bg-surface-hover"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(s.id)}
                    onChange={() => toggleUser(s.id)}
                    className="h-4 w-4 rounded border-line text-brand-500 focus:ring-brand-400"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-ink">
                        {s.full_name}
                      </span>
                      {s.role && (
                        <span
                          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                            ROLE_BADGE_CLASSES[s.role] ??
                            "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {roleDisplayName(s.role)}
                        </span>
                      )}
                    </div>
                    {s.email && (
                      <div className="truncate text-xs text-ink-subtle">
                        {s.email}
                      </div>
                    )}
                  </div>
                  {s.code && (
                    <span className="text-xs text-ink-subtle">{s.code}</span>
                  )}
                </label>
              ))
            )}
          </div>
        </Card>

        {/* Right: Message form */}
        <Card extra="p-5">
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("sendNotif.titleLabel")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("sendNotif.titlePlaceholder")}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            />
          </div>

          <div className="mb-6">
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("sendNotif.messageLabel")}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("sendNotif.messagePlaceholder")}
              rows={6}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            />
          </div>

          <div className="mb-6">
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("notifications.scheduleLabel")}{" "}
              <span className="text-ink-subtle">
                {t("notifications.optional")}
              </span>
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
            />
            {scheduledAt && (
              <p className="mt-1 text-xs text-brand-500">
                {t("notifications.scheduledAt", {
                  time: new Date(scheduledAt).toLocaleString(locale),
                })}
              </p>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={sending}
            className="w-full rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
          >
            {sending
              ? t("sendNotif.sendingBtn")
              : scheduledAt
              ? t("notifications.scheduleSendAt", {
                  time: new Date(scheduledAt).toLocaleTimeString(locale, {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                  date: new Date(scheduledAt).toLocaleDateString(locale),
                })
              : t("sendNotif.sendBtn")}
          </button>
        </Card>
      </div>
    </div>
  );
};

export default SendNotificationPage;
